import { LayoutGrid, Check, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

/**
 * Lets a user holding more than one role hop between their dashboards
 * (Mentor Portal ↔ Investor Portal ↔ Admin Console, …) without re-auth.
 * Hidden for single-role accounts.
 */
export const WorkspaceSwitcher = () => {
  const { user, switchWorkspace } = useAuth();
  const ws = user?.available_workspaces || [];
  if (ws.length < 2) return null;
  const active = ws.find((w) => w.active) || ws[0];

  const onPick = async (w) => {
    if (w.active) return;
    try {
      await switchWorkspace(w.role_id);
      window.location.href = w.route;
    } catch (e) {
      toast.error('Could not switch workspace', { description: e.message });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5 rounded-full h-9" data-testid="workspace-switcher">
          <LayoutGrid className="h-4 w-4" />
          <span className="hidden sm:inline text-xs font-medium">{active?.workspace_label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" data-testid="workspace-switcher-menu">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">Switch workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {ws.map((w) => (
          <DropdownMenuItem
            key={w.role_id}
            onClick={() => onPick(w)}
            data-testid={`workspace-opt-${w.workspace}`}
            className="gap-2 cursor-pointer"
          >
            <span className="flex-1 min-w-0">
              {w.workspace_label}
              <span className="block text-[11px] text-muted-foreground truncate">{w.role_label}</span>
            </span>
            {w.active && <Check className="h-4 w-4 text-primary shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default WorkspaceSwitcher;
